import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Select, DatePicker, Button, Tooltip, Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { fetchDataUpdate } from './api';
import './DataUpdate.css';
import BasicLayout from '../../components/layout/BasicLayout';

const { Option } = Select;

const DATE_TIME_FMT = 'DD.MM.YYYY HH:mm';
const DATE_FMT = 'DD.MM.YYYY';
const API_DATE_FMT = 'YYYY-MM-DD';

const EMPTY_FILTERS = { result: 'all', module: 'all', from: null, to: null };

const SHOW_UNIFICATION_BADGE = true;

export default function DataUpdate() {
  const { t, i18n } = useTranslation();
  // Русский код — 'ru', всё остальное (kz/kk) считаем казахским: влияет на
  // выбор module_name_kk/ru и source_name_kk/ru.
  const lang = (i18n.language || '').toLowerCase().startsWith('ru') ? 'ru' : 'kk';

  const [catalog, setCatalog] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback((f) => {
    setLoading(true);
    setLoadError(null);
    fetchDataUpdate({
      result: f.result,
      module: f.module,
      from: f.from ? f.from.format(API_DATE_FMT) : null,
      to: f.to ? f.to.format(API_DATE_FMT) : null,
    })
      .then((data) => {
        const rws = Array.isArray(data && data.rows) ? data.rows : [];
        setRows(rws);
        setSummary((data && data.summary) || null);
        const unfiltered = f.result === 'all' && f.module === 'all' && !f.from && !f.to;
        if (unfiltered) setCatalog((prev) => (prev.length ? prev : rws));
      })
      .catch(() => {
        setRows([]);
        setSummary(null);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filters);
  }, [filters, load]);

  const patch = (next) => setFilters((prev) => ({ ...prev, ...next }));

  const onFrom = (value) =>
    patch({
      from: value,
      to: filters.to && value && value.isAfter(filters.to) ? value : filters.to,
    });

  const onTo = (value) =>
    patch({
      to: value,
      from: filters.from && value && value.isBefore(filters.from) ? value : filters.from,
    });

  const moduleOptions = useMemo(
    () =>
      catalog.map((r) => ({
        code: r.module_code,
        name: lang === 'kk' ? r.module_name_kk : r.module_name_ru,
      })),
    [catalog, lang]
  );

  // Подпись результата по значению ('success'|'error'|'no_data')
  const resultLabel = useCallback((result) => t(`dataUpdate.${resultKey(result)}`), [t]);

  const filterCaption = useMemo(() => {
    const parts = [];
    if (filters.result !== 'all') {
      parts.push(`${t('dataUpdate.filterResult')} — ${resultLabel(filters.result)}`);
    }
    if (filters.module !== 'all') {
      const found = moduleOptions.find((m) => m.code === filters.module);
      if (found) parts.push(`${t('dataUpdate.filterModule')} — ${found.name}`);
    }
    if (filters.from || filters.to) {
      const a = filters.from ? filters.from.format(DATE_FMT) : '…';
      const b = filters.to ? filters.to.format(DATE_FMT) : '…';
      parts.push(`${t('dataUpdate.filterPeriod')} ${a} — ${b}`);
    }
    return parts.length ? `· ${t('dataUpdate.filterPrefix')}: ${parts.join('; ')}` : '';
  }, [filters, moduleOptions, t, resultLabel]);

  const isDirty =
    filters.result !== 'all' || filters.module !== 'all' || filters.from || filters.to;

  const totalModules = num(summary, 'total_modules');

  const columns = [
    {
      title: t('dataUpdate.colModule'),
      key: 'module',
      width: '22%',
      render: (_, row) => pick(row, 'module_name', lang),
    },
    {
      title: t('dataUpdate.colSource'),
      key: 'source',
      width: '32%',
      render: (_, row) => <span className="du-source">{pick(row, 'source_name', lang)}</span>,
    },
    {
      title: t('dataUpdate.colDate'),
      key: 'date',
      width: '18%',
      render: (_, row) =>
        row.last_update ? (
          moment(row.last_update).format(DATE_TIME_FMT)
        ) : (
          <span className="du-dash">{t('dataUpdate.dash')}</span>
        ),
    },
    {
      title: t('dataUpdate.colCount'),
      key: 'count',
      width: '16%',
      align: 'right',
      render: (_, row) =>
        row.records_count == null ? (
          <span className="du-dash">{t('dataUpdate.dash')}</span>
        ) : (
          <span className="du-num">{formatNumber(row.records_count)}</span>
        ),
    },
    {
      title: t('dataUpdate.colResult'),
      key: 'result',
      width: '12%',
      render: (_, row) => (
        <span className="du-result">
          <i className={`du-dot du-dot--${row.result}`} />
          {resultLabel(row.result)}
          {row.result === 'error' && row.error_reason ? (
            <Tooltip title={row.error_reason}>
              <InfoCircleOutlined className="du-reason" />
            </Tooltip>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <BasicLayout>
      <div className="du-page">
        <h2 className="du-title">
          {t('dataUpdate.pageTitle')}
          <span className="du-title-filter">{filterCaption}</span>
        </h2>

        <div className="du-card du-toolbar">
          <div className="du-toolbar-row">
            <div className="du-field">
              <label className="du-label">{t('dataUpdate.filterResult')}</label>
              <Select
                value={filters.result}
                onChange={(v) => patch({ result: v })}
                style={{ width: 180 }}
                dropdownClassName="du-dropdown"
              >
                <Option value="all">{t('dataUpdate.all')}</Option>
                <Option value="success">{t('dataUpdate.success')}</Option>
                <Option value="error">{t('dataUpdate.error')}</Option>
                <Option value="no_data">{t('dataUpdate.noData')}</Option>
              </Select>
            </div>

            <div className="du-field">
              <label className="du-label">{t('dataUpdate.filterModule')}</label>
              <Select
                value={filters.module}
                onChange={(v) => patch({ module: v })}
                style={{ width: 300 }}
                dropdownClassName="du-dropdown"
              >
                <Option value="all">{t('dataUpdate.all')}</Option>
                {moduleOptions.map((m) => (
                  <Option key={m.code} value={m.code}>
                    {m.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="du-field">
              <label className="du-label">{t('dataUpdate.dateFrom')}</label>
              <DatePicker
                value={filters.from}
                onChange={onFrom}
                format={DATE_FMT}
                placeholder={DATE_FMT.toLowerCase()}
                style={{ width: 160 }}
                dropdownClassName="du-dropdown"
              />
            </div>

            <div className="du-field">
              <label className="du-label">{t('dataUpdate.dateTo')}</label>
              <DatePicker
                value={filters.to}
                onChange={onTo}
                format={DATE_FMT}
                placeholder={DATE_FMT.toLowerCase()}
                style={{ width: 160 }}
                dropdownClassName="du-dropdown"
              />
            </div>

            <div className="du-field du-field--action">
              <Button onClick={() => setFilters(EMPTY_FILTERS)} disabled={!isDirty}>
                {t('dataUpdate.reset')}
              </Button>
            </div>
          </div>

          <div className="du-toolbar-hint">{t('dataUpdate.dateHint')}</div>
        </div>

        <div className="du-counters">
          <Counter
            label={t('dataUpdate.cntTotal')}
            value={totalModules}
            badge={SHOW_UNIFICATION_BADGE ? t('dataUpdate.badgeUnification') : null}
          />
          <Counter
            label={t('dataUpdate.cntSuccess')}
            value={num(summary, 'updated_success')}
            tone="success"
          />
          <Counter
            label={t('dataUpdate.cntError')}
            value={num(summary, 'updated_error')}
            tone="error"
          />
          <Counter
            label={t('dataUpdate.cntNever')}
            value={num(summary, 'never_updated')}
            tone="muted"
          />
        </div>

        <div className="du-card du-legend">
          <div className="du-legend-row">
            <span className="du-legend-item">
              <i className="du-dot du-dot--success" />
              {t('dataUpdate.legendSuccess')}
            </span>
            <span className="du-legend-item">
              <i className="du-dot du-dot--error" />
              {t('dataUpdate.legendError')}
            </span>
            <span className="du-legend-item">
              <i className="du-dot du-dot--no_data" />
              {t('dataUpdate.legendNoData')}
            </span>
          </div>
          <div className="du-legend-note">{t('dataUpdate.legendNote')}</div>
        </div>

        {loadError ? (
          <Alert
            type="warning"
            showIcon
            className="du-alert"
            message={t('dataUpdate.loadError')}
            action={
              <Button size="small" onClick={() => load(filters)}>
                {t('dataUpdate.retry')}
              </Button>
            }
          />
        ) : null}

        <div className="du-card du-table-card">
          <Table
            className="du-table"
            rowKey="module_code"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `${total} / ${totalModules}`,
            }}
            locale={{ emptyText: t('dataUpdate.empty') }}
          />
        </div>
      </div>
    </BasicLayout>
  );
}

function Counter({ label, value, tone, badge }) {
  return (
    <div className={`du-card du-counter${tone ? ` du-counter--${tone}` : ''}`}>
      <div className="du-counter-value">{formatNumber(value)}</div>
      <div className="du-counter-label">
        {label}
        {badge ? <span className="du-badge">{badge}</span> : null}
      </div>
    </div>
  );
}

function pick(row, prefix, lang) {
  return row[`${prefix}_${lang === 'kk' ? 'kk' : 'ru'}`] || '';
}

function resultKey(result) {
  if (result === 'success') return 'success';
  if (result === 'error') return 'error';
  return 'noData';
}

function num(summary, key) {
  const v = summary ? summary[key] : 0;
  return Number.isFinite(v) ? v : 0;
}

function formatNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) return '0';
  return new Intl.NumberFormat('ru-RU').format(Number(value));
}
